import { useEffect, useState } from 'react';
import _ from 'lodash';
import { bool, func, instanceOf, string } from 'prop-types';
import { KeyboardDatePicker, KeyboardTimePicker } from '@material-ui/pickers';
import { Schedule as ScheduleIcon } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment-timezone';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';

import DATE_FORMATS from '../../../utilities/DateTimeFormats';
import { useTimezone, getMeridiemTimeFormat } from '../../../stores/SessionStore';

const DATE = t`Date`;
const TIME = t`Time`;

const useStyles = makeStyles((theme) => ({
    row: {
        ...theme.mixins.flex.displayInlineFlex,
        ...theme.mixins.flex.flexDirectionRow,
        ...theme.mixins.flex.justifyContentCenter,
        marginBottom: theme.spacing(2),
        marginLeft: theme.spacing(2),

        '& .MuiTextField-root:first-child': {
            marginRight: theme.spacing(2),
        },

        '& .MuiTextField-root': {
            '& .MuiFormLabel-root': {
                visibility: (props) => {
                    return props?.hideLabels ? 'hidden' : 'visible';
                },
            },
        },
    },
}));

function DateTime(props) {
    const [userTimeZone] = useTimezone();
    const { label, value, disabled, error, onChange, onValidChange, timeZone = userTimeZone } = props;

    const [dateTime, setDateTime] = useState(moment(value).tz(timeZone));
    const [dateValid, setDateValid] = useState(true);
    const [timeValid, setTimeValid] = useState(true);

    const classes = useStyles(props);

    useEffect(() => {
        setDateTime(moment(value).tz(timeZone));
    }, [value, timeZone]);

    useEffect(() => {
        onValidChange(dateValid && timeValid);
    }, [dateValid, timeValid, onValidChange]);

    function handleDateChange(newDate) {
        if (newDate && newDate.isValid()) {
            setDateValid(true);
            const newValue = moment()
                .tz(timeZone)
                .year(newDate.year())
                .month(newDate.month())
                .date(newDate.date())
                .hours(dateTime.hours())
                .minutes(dateTime.minutes())
                .milliseconds(0);

            setDateTime(newValue);
            onChange(newValue.toDate());
        } else {
            setDateValid(false);
        }
    }

    function handleTimeChange(newTime) {
        if (newTime && newTime.isValid()) {
            setTimeValid(true);
            const newValue = moment()
                .tz(timeZone)
                .year(dateTime.year())
                .month(dateTime.month())
                .date(dateTime.date())
                .hours(newTime.hours())
                .minutes(newTime.minutes())
                .milliseconds(0);

            setDateTime(newValue);
            onChange(newValue.toDate());
        } else {
            setTimeValid(false);
        }
    }

    return (
        <div className={classes.row}>
            <KeyboardDatePicker
                id={`${label.toLowerCase()}-date`}
                label={`${label} ${i18n._(DATE)}`}
                variant="inline"
                maskChar="-"
                mask="--/--/----"
                autoOk
                value={dateTime}
                onChange={handleDateChange}
                format={DATE_FORMATS.DATE_FIELD}
                disabled={disabled}
                error={error || !dateValid}
            />
            <KeyboardTimePicker
                id={`${label.toLowerCase()}-time`}
                label={`${label} ${i18n._(TIME)}`}
                variant="inline"
                maskChar="-"
                mask="--:-- -M"
                autoOk
                keyboardIcon={<ScheduleIcon />}
                value={dateTime}
                onChange={handleTimeChange}
                format={getMeridiemTimeFormat() ? DATE_FORMATS.TIME_MIN_12H : DATE_FORMATS.TIME_MIN_24H}
                disabled={disabled}
                error={error || !timeValid}
            />
        </div>
    );
}

DateTime.propTypes = {
    label: string.isRequired,
    value: instanceOf(Date).isRequired,
    disabled: bool,
    error: bool,
    onChange: func,
    onValidChange: func,
    // hideLabels: bool,    <- NJA: This is used, but only in useStyles, so eslint is forcing me to omit it here
};

DateTime.defaultProps = {
    disabled: false,
    error: false,
    onChange: _.noop,
    onValidChange: _.noop,
    // hideLabels: false,
};

export default DateTime;
